import React, { useEffect, useState, useRef } from 'react';
import { Carousel, CarouselItem, CarouselControl, CarouselIndicators } from 'reactstrap';
import { IMediaData } from '../../../../modules/dobbies-media-gallery/utils/helper';
interface ICustomCarouselProps {
    mediaGalleryItems: IMediaData[];
    onNext?: () => void;
    onPrevious?: () => void;
    onGoToIndex?: (index: number) => void;
    thumbnailActiveIndex?: number;
    /** Optional callback when a slide is clicked/tapped (not swiped). Used for navigation. */
    onSlideClick?: () => void;
}

export function CustomCarousel(props: ICustomCarouselProps) {
    const { mediaGalleryItems, thumbnailActiveIndex, onNext, onPrevious, onGoToIndex, onSlideClick } = props;

    const [activeIndex, setActiveIndex] = useState(0);
    const [animating, setAnimating] = useState(false);

    const startX = useRef(0);
    const startY = useRef(0);
    const isHorizontalSwipe = useRef(false);
    const isSwiping = useRef(false);
    const SWIPE_THRESHOLD = 10; // pixels - movement below this is considered a tap

    const handleTouchStart = (e: React.TouchEvent) => {
        startX.current = e.touches[0].clientX;
        startY.current = e.touches[0].clientY;
        isHorizontalSwipe.current = false;
        isSwiping.current = false;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        const dx = Math.abs(e.touches[0].clientX - startX.current);
        const dy = Math.abs(e.touches[0].clientY - startY.current);

        // Mark as swiping if movement exceeds threshold
        if (dx > SWIPE_THRESHOLD || dy > SWIPE_THRESHOLD) {
            isSwiping.current = true;
        }

        if (dx > dy) {
            isHorizontalSwipe.current = true;
            e.preventDefault(); // stop page scroll
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        // Don't trigger click if tapping on carousel controls (arrows, indicators)
        const target = e.target as HTMLElement;
        const isControlTap =
            target.closest('.carousel-control-prev') || target.closest('.carousel-control-next') || target.closest('.carousel-indicators');

        // Only trigger click if it was a tap (not a swipe) and not on controls
        if (!isSwiping.current && !isControlTap && onSlideClick) {
            onSlideClick();
        }
    };

    // Mouse event handlers for desktop click detection
    const handleMouseDown = (e: React.MouseEvent) => {
        startX.current = e.clientX;
        startY.current = e.clientY;
        isSwiping.current = false;
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (e.buttons === 1) {
            // Left mouse button is pressed
            const dx = Math.abs(e.clientX - startX.current);
            const dy = Math.abs(e.clientY - startY.current);
            if (dx > SWIPE_THRESHOLD || dy > SWIPE_THRESHOLD) {
                isSwiping.current = true;
            }
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        // Don't trigger click if clicking on carousel controls (arrows, indicators)
        const target = e.target as HTMLElement;
        const isControlClick =
            target.closest('.carousel-control-prev') || target.closest('.carousel-control-next') || target.closest('.carousel-indicators');

        // Only trigger click if it was a click (not a drag) and not on controls
        if (!isSwiping.current && !isControlClick && onSlideClick) {
            onSlideClick();
        }
    };

    const next = () => {
        if (animating) return;
        const nextIndex = activeIndex === mediaGalleryItems.length - 1 ? 0 : activeIndex + 1;
        setActiveIndex(nextIndex);
        onNext && onNext();
    };

    const previous = () => {
        if (animating) return;
        const nextIndex = activeIndex === 0 ? mediaGalleryItems.length - 1 : activeIndex - 1;
        setActiveIndex(nextIndex);
        onPrevious && onPrevious();
    };

    const goToIndex = (newIndex: number) => {
        if (animating) return;
        setActiveIndex(newIndex);
        onGoToIndex && onGoToIndex(newIndex);
    };

    useEffect(() => {
        if (thumbnailActiveIndex !== undefined && thumbnailActiveIndex !== activeIndex) {
            goToIndex(thumbnailActiveIndex);
        }
    }, [thumbnailActiveIndex]);

    const slides = mediaGalleryItems.map(item => (
        <CarouselItem tag='div' key={item.src} onExiting={() => setAnimating(true)} onExited={() => setAnimating(false)}>
            <img src={item.src} alt={`A photo of ${item.altText}`} width='426' height='596' {...{ fetchpriority: 'high' }} />
        </CarouselItem>
    ));

    return (
        <div
            className={`ms-media-gallery__carousel${onSlideClick ? ' ms-media-gallery__carousel--clickable' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={onSlideClick ? handleTouchEnd : undefined}
            onMouseDown={onSlideClick ? handleMouseDown : undefined}
            onMouseMove={onSlideClick ? handleMouseMove : undefined}
            onMouseUp={onSlideClick ? handleMouseUp : undefined}
        >
            <Carousel activeIndex={activeIndex} next={next} previous={previous} interval={false}>
                {mediaGalleryItems.length > 1 && (
                    // @ts-ignore
                    <CarouselIndicators items={mediaGalleryItems} activeIndex={activeIndex} onClickHandler={goToIndex} />
                )}
                {slides}
                {mediaGalleryItems.length > 1 ? (
                    <>
                        <CarouselControl direction='prev' directionText='Previous' onClickHandler={previous} />
                        <CarouselControl direction='next' directionText='Next' onClickHandler={next} />
                    </>
                ) : null}
            </Carousel>
        </div>
    );
}
